/*
 * Judaica: Shabbat times in the top bar (sections/jc-top-bar.liquid), ported
 * from judaica.com's shabbat-times-v2.js. Shows the coming (or current)
 * Shabbat's candle lighting time and when Shabbat ends.
 *
 * Location is what the visitor chose in the "Change location" dialog, a ZIP
 * code or their browser location, remembered in this browser. Until then the
 * widget shows a "Find my time" link that opens the dialog.
 *
 * Times follow Chabad.org's rules: Shabbat starts 18 minutes before Friday's
 * sunset (40 in Jerusalem) and ends when the sun is 8.5 degrees below the
 * horizon on Saturday, both rounded to the nearest minute. Sunset and the 8.5
 * degree time come to the second from Hebcal's zmanim API; its Shabbat API
 * truncates instead of rounding, which put it a minute off Chabad.org.
 */
if (!customElements.get('jc-shabbat-times')) {
  const ZMANIM_URL = 'https://www.hebcal.com/zmanim';
  const REVERSE_GEOCODE_URL = 'https://api.bigdatacloud.net/data/reverse-geocode-client';
  const STORAGE_KEY = 'jc-shabbat-times';
  const CACHE_MS = 6 * 60 * 60 * 1000;
  const ROTATE_MS = 4000;
  const PIN_ICON =
    '<svg class="jc-candles__pin" viewBox="0 0 12 16" aria-hidden="true" focusable="false">' +
    '<path fill="currentColor" d="M6 0a6 6 0 0 0-6 6c0 4.5 6 10 6 10s6-5.5 6-10a6 6 0 0 0-6-6zm0 8.5A2.5 2.5 0 1 1 6 3.5a2.5 2.5 0 0 1 0 5z"/>' +
    '</svg>';

  function load() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch (e) {
      return {};
    }
  }

  function save(state) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      // Private mode or storage full: it still works, it just asks again next visit.
    }
  }

  function browserTimeZone() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (e) {
      return '';
    }
  }

  async function getJson(url, params, timeout) {
    const query = new URLSearchParams(params);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(`${url}?${query}`, { signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.json();
    } finally {
      clearTimeout(timer);
    }
  }

  // "2026-10-23T18:45:42-04:00" -> seconds after midnight, read off the string
  // so it stays in the location's time zone.
  function secondsOfDay(iso) {
    return +iso.slice(11, 13) * 3600 + +iso.slice(14, 16) * 60 + (+iso.slice(17, 19) || 0);
  }

  // Seconds after midnight -> "6:28 PM", rounded to the nearest minute as Chabad.org does.
  function formatClock(seconds) {
    const total = Math.round(seconds / 60);
    const hours = Math.floor(total / 60) % 24;
    const minutes = String(total % 60).padStart(2, '0');
    return `${hours % 12 || 12}:${minutes} ${hours < 12 ? 'AM' : 'PM'}`;
  }

  // "2026-10-16T..." -> "Fri, Oct 16"
  function formatDate(iso) {
    const date = new Date(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10));
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }

  function isoDate(date) {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  // The coming Shabbat's Friday and Saturday on the visitor's calendar (on Saturday, yesterday and today).
  function shabbatDates() {
    const today = new Date();
    const offset = today.getDay() === 6 ? -1 : 5 - today.getDay();
    const friday = new Date(today.getFullYear(), today.getMonth(), today.getDate() + offset);
    const saturday = new Date(friday.getFullYear(), friday.getMonth(), friday.getDate() + 1);
    return { fri: isoDate(friday), sat: isoDate(saturday) };
  }

  // Minutes before sunset: 40 in Jerusalem, 18 elsewhere.
  function candleMinutes(location) {
    const lat = +location.latitude;
    const lng = +location.longitude;
    return lat > 31.7 && lat < 31.86 && lng > 35.14 && lng < 35.3 ? 40 : 18;
  }

  // Reduce a Hebcal zmanim response (start=Friday, end=Saturday) to what the widget shows.
  function parseZmanim(response, dates, withPlace) {
    const times = response.times || {};
    const location = response.location || {};
    const sunset = (times.sunset || {})[dates.fri];
    const nightfall = (times.tzeit85deg || {})[dates.sat];
    if (!sunset) return null;

    return {
      date: formatDate(sunset),
      time: formatClock(secondsOfDay(sunset) - candleMinutes(location) * 60),
      endDate: nightfall ? formatDate(nightfall) : '',
      endTime: nightfall ? formatClock(secondsOfDay(nightfall)) : '',
      place: withPlace ? [location.city, location.state].filter(Boolean).join(', ') : '',
    };
  }

  // "Pompano Beach, FL" (or "Tel Aviv, Israel") from a BigDataCloud response.
  function formatPlace(geo) {
    // "city" is the metro area (Pompano Beach -> Miami); "locality" is the town itself.
    const city = geo.locality || geo.city || '';
    const code = geo.principalSubdivisionCode || '';
    const region = code.startsWith('US-') ? code.slice(3) : geo.countryName || '';
    return [city, region].filter(Boolean).join(', ');
  }

  function span(className, text) {
    const el = document.createElement('span');
    el.className = className;
    el.textContent = text;
    return el;
  }

  customElements.define(
    'jc-shabbat-times',
    class extends HTMLElement {
      connectedCallback() {
        this.body = this.querySelector('[data-body]');
        this.locate = this.querySelector('[data-locate]');
        // The dialog sits outside this element, so its clicks don't reopen it.
        this.dialog = this.parentElement.querySelector('[data-shabbat-dialog]');
        if (!this.body || !this.locate || !this.dialog) return;
        this.state = load();

        this.addEventListener('click', () => this.openDialog());

        const form = this.dialog.querySelector('form');
        this.zipInput = this.dialog.querySelector('input');
        this.zipErrorEl = this.dialog.querySelector('[data-zip-error]');
        form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.submitZip();
        });
        this.dialog.addEventListener('click', (event) => {
          // A click on the dialog element itself is a click on the backdrop.
          if (event.target === this.dialog || event.target.closest('[data-shabbat-close]')) this.dialog.close();
          if (event.target.closest('[data-zip-geo]')) {
            this.dialog.close();
            this.requestBrowserLocation();
          }
        });

        if (this.state.zip) {
          this.showForZip(this.state.zip);
        } else if (this.state.coords) {
          this.showForCoords(this.state.coords);
        } else {
          this.render(null);
        }
      }

      disconnectedCallback() {
        clearInterval(this.rotateTimer);
      }

      // "Shabbos Starts", alternating with the town every few seconds. Both sit
      // in one grid cell sized to the wider, so the times never shift. Screen
      // readers get the static wording; reduced motion keeps "Shabbos Starts".
      startsLabel(place) {
        const label = span('jc-candles__starts', '');
        const rotator = span('jc-candles__rotator', '');
        rotator.setAttribute('aria-hidden', 'true');
        rotator.append(span('jc-candles__rotator-item is-shown', 'Shabbos Starts'));
        label.append(span('visually-hidden', 'Shabbos starts'), rotator);

        if (place && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
          rotator.append(span('jc-candles__rotator-item', place));
          this.rotateTimer = setInterval(() => {
            [...rotator.children].forEach((item) => item.classList.toggle('is-shown'));
          }, ROTATE_MS);
        }
        return label;
      }

      render(result) {
        clearInterval(this.rotateTimer);
        this.body.replaceChildren();

        if (result) {
          // Kept short to share the top bar; the full dates are in the tooltip.
          this.body.append(this.startsLabel(result.place), ' ', span('jc-candles__time', result.time));
          if (result.endTime) this.body.append(' · Ends ', span('jc-candles__time', result.endTime));

          const where = result.place ? ` for ${result.place}` : '';
          const end = result.endTime ? ` · Shabbos ends ${result.endDate}, ${result.endTime}` : '';
          this.title = `Shabbos times${where}: Shabbos starts ${result.date}, ${result.time}${end}. Click to change location.`;

          // The top bar is tight next to a time, so "Change location" is a pin icon.
          this.locate.innerHTML = PIN_ICON;
          this.locate.title = 'Change location';
          this.locate.setAttribute('aria-label', 'Change location');
        } else {
          this.setLocateText('Find my time');
        }
        this.classList.toggle('jc-candles--has-time', !!result);
        this.hidden = false;
      }

      setLocateText(text) {
        this.locate.textContent = text;
        this.locate.removeAttribute('title');
        this.locate.removeAttribute('aria-label');
      }

      showMessage(text) {
        clearInterval(this.rotateTimer);
        this.body.replaceChildren(span('jc-candles__message', text));
        this.setLocateText('Change location');
        this.classList.remove('jc-candles--has-time');
        this.hidden = false;
      }

      // Fetch (or reuse cached) times for a Hebcal zmanim query ({zip} or
      // {latitude, longitude, tzid}). The cache key carries the week.
      async fetchTimes(query, key) {
        const dates = shabbatDates();
        const cacheKey = `${dates.fri}:${key}`;
        const { state } = this;
        if (state.result && state.cacheKey === cacheKey && state.expires > Date.now()) return state.result;

        const params = { cfg: 'json', sec: 1, start: dates.fri, end: dates.sat, ...query };
        const result = parseZmanim(await getJson(ZMANIM_URL, params, 10000), dates, !!query.zip);
        if (!result) throw new Error('No times for this location');

        state.result = result;
        state.cacheKey = cacheKey;
        state.expires = Date.now() + CACHE_MS;
        save(state);
        return result;
      }

      async showForCoords(coords) {
        let result;
        try {
          result = await this.fetchTimes(
            { latitude: coords.lat, longitude: coords.lng, tzid: coords.tzid || browserTimeZone() },
            `pos:${coords.lat},${coords.lng}`
          );
        } catch (error) {
          this.showMessage('Times unavailable right now');
          return;
        }
        this.render(result);
        if (result.place) return;

        // Best-effort city name; Hebcal only returns degrees for coordinates.
        try {
          const geo = await getJson(
            REVERSE_GEOCODE_URL,
            { latitude: coords.lat, longitude: coords.lng, localityLanguage: 'en' },
            5000
          );
          result.place = formatPlace(geo);
          save(this.state);
          this.render(result);
        } catch (error) {
          // The times are already showing; only the town name is missing.
        }
      }

      async showForZip(zip) {
        try {
          this.render(await this.fetchTimes({ zip }, `zip:${zip}`));
        } catch (error) {
          this.render(null);
        }
      }

      requestBrowserLocation() {
        if (!navigator.geolocation) {
          this.showMessage('Location not supported by this browser');
          return;
        }
        this.setLocateText('Locating…');
        navigator.geolocation.getCurrentPosition(
          (position) => {
            // Two decimals (~1 km) is plenty for sunset and keeps the exact spot private.
            this.state.coords = {
              lat: Math.round(position.coords.latitude * 100) / 100,
              lng: Math.round(position.coords.longitude * 100) / 100,
              tzid: browserTimeZone(),
            };
            delete this.state.zip;
            save(this.state);
            this.showForCoords(this.state.coords);
          },
          (error) => {
            delete this.state.coords;
            save(this.state);
            this.showMessage(
              error.code === error.PERMISSION_DENIED ? 'Location access is blocked' : "Couldn't find your location"
            );
          },
          { timeout: 15000, maximumAge: 60 * 60 * 1000 }
        );
      }

      openDialog() {
        this.zipError('');
        this.zipInput.value = this.state.zip || '';
        this.dialog.showModal();
        this.zipInput.focus();
      }

      zipError(text) {
        this.zipErrorEl.textContent = text;
        this.zipErrorEl.hidden = !text;
        this.zipInput.toggleAttribute('aria-invalid', !!text);
      }

      async submitZip() {
        const zip = this.zipInput.value.trim();
        if (!/^\d{5}$/.test(zip)) {
          this.zipError('Please enter a 5-digit ZIP code.');
          return;
        }
        try {
          const result = await this.fetchTimes({ zip }, `zip:${zip}`);
          this.state.zip = zip;
          delete this.state.coords;
          save(this.state);
          this.render(result);
          this.dialog.close();
        } catch (error) {
          this.zipError("We couldn't find Shabbat times for that ZIP code.");
        }
      }
    }
  );
}
